import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "hooks/useApi";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useNotification } from "./useNotification";

type Props = {
  entityId: string;
  seasonId: string;
};

export const useCopyStaffFromSeasonToSeason = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ entityId, seasonId, fromSeasonId }: Props & { fromSeasonId: string }) =>
      callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/staff/copy/from/season/${fromSeasonId}`,
        { method: "POST", body: undefined }
      ),
    onSuccess: (response: any, { entityId, seasonId }: Props & { fromSeasonId: string }) => {
      queryClient.setQueryData(["seasonStaff", sport, organizationId, entityId, seasonId], () => response);
      queryClient.invalidateQueries({
        queryKey: ["seasons", sport, organizationId, entityId],
      });
      queryClient.invalidateQueries({
        queryKey: ["season", sport, organizationId, entityId, seasonId],
      });
      showSuccessNotification(formatMessage({ id: "notification.staff.copied" }));
    },
  });
};

export const useCopyStaffFromFixtureToFixture = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({
      entityId,
      seasonId,
      fixtureId,
      fromFixtureId,
    }: Props & { fixtureId: string; fromFixtureId: string }) =>
      callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/staff/copy/from/fixture/${fromFixtureId}`,
        { method: "POST", body: undefined }
      ),
    onSuccess: (
      response: any,
      { entityId, seasonId, fixtureId }: Props & { fixtureId: string; fromFixtureId: string }
    ) => {
      queryClient.setQueryData(["fixtureStaff", sport, organizationId, entityId, seasonId, fixtureId], () => response);
      queryClient.invalidateQueries({
        queryKey: ["fixtures", sport, organizationId, entityId, seasonId],
      });
      queryClient.invalidateQueries({
        queryKey: ["fixture", sport, organizationId, entityId, seasonId, fixtureId],
      });
      showSuccessNotification(formatMessage({ id: "notification.staff.copied" }));
    },
  });
};
